import { Autocomplete } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { CustomerBadge } from '@vend/react-utilities'
import { ApiCustomer } from '@vend/types'
import { trackUserEvent } from '@vend/utilities'
import React, { FunctionComponent, useEffect, useState } from 'react'
import { isValidCustomer } from '../../../functions/sale'
import { UpdateSaleCustomerModal } from '../../../modals/update-customer-modal'
import {
  useCustomerManager,
  useHotkeysService,
  useSaleManager,
} from '../../../ng-compat/hooks'
import { $rootScope } from '../../../ng-compat/imports'
import { useAppSelector } from '../../../state/hooks'
import { selectSaleCustomer } from '../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { CustomerSearch } from '../../customer/customer-search/CustomerSearch'
import { ViewCustomerModal } from '../../react/business-components/CustomerModal/ViewCustomerModal'
import { PartialCustomerGroup } from '../../react/modals/common/types'
import { WEB_REGISTER } from '../../web-register.constant'
import { UnfulfilledSaleCustomerError } from '../sale-unfulfilled/UnfulfilledSaleCustomerError'
import { LockedCustomerIcon } from './LockedCustomerIcon'
import { RemoveCustomerButton } from './RemoveCustomerButton'

const HOTKEY = 'c'

interface SaleCustomerSearchProps {
  canViewDetails?: boolean
}

export const SaleCustomerSearch: FunctionComponent<SaleCustomerSearchProps> = ({
  canViewDetails = true,
}) => {
  const t = useT()
  const hotkeyService = useHotkeysService()
  const saleManager = useSaleManager()
  const customerManager = useCustomerManager()
  const getCustomerGroup = customerManager.getCustomerGroup
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const [customerGroup, setCustomerGroup] = useState<
    PartialCustomerGroup | undefined
  >()
  const [focusOnInput, setFocusOnInput] = useState<boolean>(false)
  const searchRef = React.createRef<Autocomplete<any>>()
  const [customerSearchQuery, setCustomerSearchQuery] = useState('')
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false)
  const openAddCustomerModal = () => {
    document.body.click()
    setShowAddCustomerModal(true)
  }
  const closeAddCustomerModal = () => setShowAddCustomerModal(false)

  useEffect(() => {
    const config = {
      combo: `alt+${HOTKEY}`,
      summary: 'Focus customer search',
      tip: 'c',
      count: true,
      action: () => {
        searchRef.current?.focusAndSelectInput()
      },
    }

    if (!isValidCustomer(saleCustomer)) {
      hotkeyService.add(config)
    }

    return () => {
      hotkeyService.remove(config.combo)
    }
  }, [saleCustomer, hotkeyService, searchRef])

  const shouldShowCustomerGroup = getFeatureFromCache('customer_groups')

  useEffect(() => {
    if (shouldShowCustomerGroup && saleCustomer) {
      getCustomerGroup(saleCustomer)
        .then(group => {
          setCustomerGroup(group || undefined)
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.warn('Failed to get customer group', error)
        })
    }
  }, [saleCustomer, shouldShowCustomerGroup, getCustomerGroup])

  const handleCustomerSelect = (customer?: ApiCustomer) => {
    if (!customer) {
      return
    }
    saleManager.addCustomer(customer).then(() => {
      $rootScope.$emit('autocomplete:customer-selected')
      $rootScope.$emit('autocomplete:global-search:focus')
    })
  }

  const handleCustomerRemoved = async () => {
    // We can't focus on the search input yet because it hasn't been rendered.
    // The ref is pointing to undefined.
    // We'll do that in the useEffect below, alongside the state focusOnInput.
    setFocusOnInput(true)
  }

  useEffect(() => {
    if (focusOnInput) {
      searchRef.current?.focusAndSelectInput()

      setFocusOnInput(false)
    }
  }, [focusOnInput, setFocusOnInput, searchRef])

  const validCustomer = isValidCustomer(saleCustomer)

  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const onBadgeClick = () => {
    if (!saleCustomer?.id) {
      return
    }
    trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Show Customer Lightbox')
    setShowCustomerModal(true)
  }

  const onCloseCustomerModal = () => {
    setShowCustomerModal(false)
    // Re-focus the global search once the customer modal is closed
    $rootScope.$emit('autocomplete:global-search:focus')
  }

  return (
    <div
      className="sale-header"
      id="wr-sale-header"
      aria-label="customer search"
    >
      {!validCustomer && (
        <div
          className="vd-pa1 wr-customer-search"
          data-hotkey-tip={HOTKEY.toUpperCase()}
        >
          <CustomerSearch
            ref={searchRef}
            onAdd={openAddCustomerModal}
            onSelect={handleCustomerSelect}
            onFetch={(query: string) => setCustomerSearchQuery(query)}
            placeholder={t('Add a customer', {
              _context: 'Sale summary customer search placeholder',
            })}
          ></CustomerSearch>
        </div>
      )}
      {validCustomer && (
        <div className="vd-mt2 vd-mb2 vd-ml4 vd-mr4 vd-flex vd-flex--align-center vd-flex--justify-between">
          {canViewDetails ? (
            <div onClick={onBadgeClick}>
              <CustomerBadge
                size="small"
                interactive="action"
                apiCustomer={saleCustomer}
                group={customerGroup}
              ></CustomerBadge>
            </div>
          ) : (
            <CustomerBadge
              size="small"
              apiCustomer={saleCustomer}
              group={customerGroup}
            ></CustomerBadge>
          )}
          <RemoveCustomerButton onCustomerRemoved={handleCustomerRemoved} />
          <LockedCustomerIcon />
        </div>
      )}
      {saleCustomer?.id && showCustomerModal && (
        <ViewCustomerModal
          onClose={onCloseCustomerModal}
          customer={saleCustomer}
        />
      )}
      <UnfulfilledSaleCustomerError />
      {showAddCustomerModal && (
        <UpdateSaleCustomerModal
          onClose={closeAddCustomerModal}
          customerSearchQuery={customerSearchQuery}
        />
      )}
    </div>
  )
}
